// PDF Generation Utility
// Uses browser's built-in print functionality with styled HTML

export const generateQuotePDF = (quote, orgSettings = {}) => {
  const formatCurrency = (amount) => `$${parseFloat(amount || 0)?.toFixed(2)}`;
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric'
    });
  };

  const products = quote?.quote_products || [];
  const subtotal = products?.reduce((sum, p) => sum + (p?.quantity * p?.price), 0);
  const brandColor = orgSettings?.primary_color || '#2563eb';
  const companyName = orgSettings?.quote_header_company_name || orgSettings?.name || 'RIGJOCKEY';
  const companyAddress = orgSettings?.quote_header_address || orgSettings?.address_line1 || '';
  const companyPhone = orgSettings?.quote_header_phone || orgSettings?.phone || '';
  const companyEmail = orgSettings?.quote_header_email || orgSettings?.email || '';
  const logoUrl = orgSettings?.logo_url || '';

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Quote ${quote?.quote_number}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; color: #1a1a2e; background: #fff; padding: 40px; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; padding-bottom: 24px; border-bottom: 3px solid ${brandColor}; }
        .company-logo { max-height: 64px; max-width: 200px; object-fit: contain; margin-bottom: 6px; }
        .company-name { font-size: 28px; font-weight: 800; color: ${brandColor}; letter-spacing: -0.5px; }
        .company-tagline { font-size: 12px; color: #6b7280; margin-top: 4px; }
        .company-info { font-size: 12px; color: #6b7280; margin-top: 2px; }
        .quote-badge { background: ${brandColor}; color: white; padding: 8px 20px; border-radius: 8px; text-align: right; }
        .quote-badge .label { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; opacity: 0.8; }
        .quote-badge .number { font-size: 20px; font-weight: 700; margin-top: 2px; }
        .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 32px; }
        .meta-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px; }
        .meta-card h3 { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #6b7280; margin-bottom: 8px; }
        .meta-card p { font-size: 14px; font-weight: 600; color: #1a1a2e; }
        .meta-card .sub { font-size: 12px; color: #6b7280; font-weight: 400; margin-top: 2px; }
        .status-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
        .status-draft { background: #f1f5f9; color: #475569; }
        .status-sent { background: #dbeafe; color: #1d4ed8; }
        .status-approved { background: #dcfce7; color: #15803d; }
        .status-rejected { background: #fee2e2; color: #dc2626; }
        .section-title { font-size: 16px; font-weight: 700; color: #1a1a2e; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 2px solid #e2e8f0; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
        thead tr { background: ${brandColor}; color: white; }
        thead th { padding: 12px 16px; text-align: left; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        tbody tr { border-bottom: 1px solid #e2e8f0; }
        tbody tr:nth-child(even) { background: #f8fafc; }
        tbody td { padding: 12px 16px; font-size: 13px; color: #374151; }
        .total-row { background: #f0f9ff !important; border-top: 2px solid ${brandColor}; }
        .total-row td { font-weight: 700; font-size: 15px; color: #1a1a2e; }
        .total-amount { color: ${brandColor}; font-size: 18px; }
        .notes-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px; margin-bottom: 24px; }
        .notes-box h3 { font-size: 12px; text-transform: uppercase; letter-spacing: 1px; color: #6b7280; margin-bottom: 8px; }
        .notes-box p { font-size: 13px; color: #374151; line-height: 1.6; }
        .approval-section { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; padding: 20px; margin-top: 32px; }
        .approval-section h3 { font-size: 14px; font-weight: 700; color: #15803d; margin-bottom: 16px; }
        .sig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-top: 16px; }
        .sig-box { border-top: 2px solid #374151; padding-top: 8px; }
        .sig-box .label { font-size: 11px; color: #6b7280; }
        .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 11px; color: #9ca3af; }
        @media print { body { padding: 20px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div>
          ${logoUrl ? `<img src="${logoUrl}" alt="Company logo" class="company-logo" />` : ''}
          <div class="company-name">${companyName}</div>
          ${companyAddress ? `<div class="company-info">${companyAddress}</div>` : ''}
          ${companyPhone ? `<div class="company-info">${companyPhone}</div>` : ''}
          ${companyEmail ? `<div class="company-info">${companyEmail}</div>` : ''}
        </div>
        <div class="quote-badge">
          <div class="label">Quote</div>
          <div class="number">${quote?.quote_number}</div>
        </div>
      </div>

      <div class="meta-grid">
        <div class="meta-card">
          <h3>Bill To</h3>
          <p>${quote?.customer?.name || 'N/A'}</p>
          <div class="sub">${quote?.customer?.email || ''}</div>
          <div class="sub">${quote?.customer?.phone || ''}</div>
        </div>
        <div class="meta-card">
          <h3>Prepared By</h3>
          <p>${quote?.salesperson?.name || 'N/A'}</p>
          <div class="sub">${quote?.salesperson?.email || ''}</div>
        </div>
        <div class="meta-card">
          <h3>Quote Date</h3>
          <p>${formatDate(quote?.created_at)}</p>
        </div>
        <div class="meta-card">
          <h3>Expected Service Date</h3>
          <p>${formatDate(quote?.expected_service_date)}</p>
        </div>
        <div class="meta-card">
          <h3>Status</h3>
          <span class="status-badge status-${(quote?.status?.name || 'draft')?.toLowerCase()}">${quote?.status?.name || 'Draft'}</span>
        </div>
        ${quote?.approval_date ? `
        <div class="meta-card">
          <h3>Approval Date</h3>
          <p>${formatDate(quote?.approval_date)}</p>
        </div>` : ''}
      </div>

      <div class="section-title">Products &amp; Services</div>
      <table>
        <thead>
          <tr>
            <th>Description</th>
            <th>Qty</th>
            <th>Unit Price</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          ${products?.map(p => `
          <tr>
            <td>${p?.name}</td>
            <td>${p?.quantity}</td>
            <td>${formatCurrency(p?.price)}</td>
            <td>${formatCurrency(p?.quantity * p?.price)}</td>
          </tr>`)?.join('')}
          <tr class="total-row">
            <td colspan="3" style="text-align:right;">Total Amount</td>
            <td class="total-amount">${formatCurrency(quote?.total_amount || subtotal)}</td>
          </tr>
        </tbody>
      </table>

      ${quote?.notes ? `
      <div class="notes-box">
        <h3>Notes</h3>
        <p>${quote?.notes}</p>
      </div>` : ''}

      ${quote?.is_pricing_exception ? `
      <div style="background:#fff7ed;border:1px solid #fed7aa;border-radius:10px;padding:16px;margin-bottom:24px;">
        <h3 style="font-size:12px;text-transform:uppercase;letter-spacing:1px;color:#c2410c;margin-bottom:8px;">&#9888; Pricing Exception</h3>
        <p style="font-size:13px;color:#374151;">${quote?.exception_reason || 'This quote contains a pricing exception requiring additional approval.'}</p>
      </div>` : ''}

      <div class="approval-section">
        <h3>Customer Acceptance</h3>
        <p style="font-size:13px;color:#374151;margin-bottom:16px;">By signing below, you agree to the terms and pricing outlined in this quote.</p>
        <div class="sig-grid">
          <div class="sig-box">
            <div class="label">Customer Signature &amp; Date</div>
          </div>
          <div class="sig-box">
            <div class="label">Authorized Representative</div>
          </div>
        </div>
      </div>

      <div class="footer">
        ${companyName} &bull; Generated ${new Date()?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
      </div>
    </body>
    </html>
  `;

  return html;
};

export const generateTicketPDF = (ticket, orgSettings = {}) => {
  const formatCurrency = (amount) => `$${parseFloat(amount || 0)?.toFixed(2)}`;
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const products = ticket?.products || [];
  const total = products?.reduce((sum, p) => sum + (parseFloat(p?.price || 0) * parseInt(p?.quantity || 1)), 0);
  const brandColor = orgSettings?.primary_color || '#7c3aed';
  const companyName = orgSettings?.ticket_header_company_name || orgSettings?.name || 'RIGJOCKEY';
  const companyAddress = orgSettings?.ticket_header_address || orgSettings?.address_line1 || '';
  const companyPhone = orgSettings?.ticket_header_phone || orgSettings?.phone || '';
  const companyEmail = orgSettings?.ticket_header_email || orgSettings?.email || '';
  const footerText = orgSettings?.ticket_footer_text || '';
  const logoUrl = orgSettings?.logo_url || '';

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Service Ticket ${ticket?.ticketId}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; color: #1a1a2e; background: #fff; padding: 40px; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; padding-bottom: 24px; border-bottom: 3px solid ${brandColor}; }
        .company-logo { max-height: 64px; max-width: 200px; object-fit: contain; margin-bottom: 6px; }
        .company-name { font-size: 28px; font-weight: 800; color: ${brandColor}; letter-spacing: -0.5px; }
        .company-info { font-size: 12px; color: #6b7280; margin-top: 2px; }
        .ticket-badge { background: ${brandColor}; color: white; padding: 8px 20px; border-radius: 8px; text-align: right; }
        .ticket-badge .label { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; opacity: 0.8; }
        .ticket-badge .number { font-size: 20px; font-weight: 700; margin-top: 2px; }
        .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 32px; }
        .meta-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px; }
        .meta-card h3 { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #6b7280; margin-bottom: 8px; }
        .meta-card p { font-size: 14px; font-weight: 600; color: #1a1a2e; }
        .meta-card .sub { font-size: 12px; color: #6b7280; font-weight: 400; margin-top: 2px; }
        .priority-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
        .priority-critical { background: #fee2e2; color: #dc2626; }
        .priority-high { background: #fef3c7; color: #d97706; }
        .priority-medium { background: #dbeafe; color: #1d4ed8; }
        .priority-low { background: #dcfce7; color: #15803d; }
        .status-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; background: #f1f5f9; color: #475569; }
        .section-title { font-size: 16px; font-weight: 700; color: #1a1a2e; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 2px solid #e2e8f0; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
        thead tr { background: ${brandColor}; color: white; }
        thead th { padding: 12px 16px; text-align: left; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        tbody tr { border-bottom: 1px solid #e2e8f0; }
        tbody tr:nth-child(even) { background: #f8fafc; }
        tbody td { padding: 12px 16px; font-size: 13px; color: #374151; }
        .total-row { background: #f5f3ff !important; border-top: 2px solid ${brandColor}; }
        .total-row td { font-weight: 700; font-size: 15px; color: #1a1a2e; }
        .total-amount { color: ${brandColor}; font-size: 18px; }
        .desc-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px; margin-bottom: 24px; }
        .desc-box h3 { font-size: 12px; text-transform: uppercase; letter-spacing: 1px; color: #6b7280; margin-bottom: 8px; }
        .desc-box p { font-size: 13px; color: #374151; line-height: 1.6; }
        .approval-section { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; padding: 20px; margin-top: 32px; }
        .approval-section h3 { font-size: 14px; font-weight: 700; color: #15803d; margin-bottom: 16px; }
        .sig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-top: 16px; }
        .sig-box { border-top: 2px solid #374151; padding-top: 8px; }
        .sig-box .label { font-size: 11px; color: #6b7280; }
        .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 11px; color: #9ca3af; }
        .tech-list { display: flex; flex-wrap: wrap; gap: 6px; }
        .tech-chip { background: #dcfce7; color: #15803d; padding: 3px 10px; border-radius: 12px; font-size: 12px; font-weight: 600; }
        @media print { body { padding: 20px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div>
          ${logoUrl ? `<img src="${logoUrl}" alt="Company logo" class="company-logo" />` : ''}
          <div class="company-name">${companyName}</div>
          ${companyAddress ? `<div class="company-info">${companyAddress}</div>` : ''}
          ${companyPhone ? `<div class="company-info">${companyPhone}</div>` : ''}
          ${companyEmail ? `<div class="company-info">${companyEmail}</div>` : ''}
        </div>
        <div class="ticket-badge">
          <div class="label">Service Ticket</div>
          <div class="number">${ticket?.ticketId}</div>
        </div>
      </div>

      <div class="meta-grid">
        <div class="meta-card">
          <h3>Customer</h3>
          <p>${ticket?.customer || 'N/A'}</p>
          <div class="sub">${ticket?.customerEmail || ''}</div>
          <div class="sub">${ticket?.customerPhone || ''}</div>
        </div>
        <div class="meta-card">
          <h3>Service Type</h3>
          <p>${ticket?.serviceType || 'N/A'}</p>
        </div>
        <div class="meta-card">
          <h3>Priority</h3>
          <span class="priority-badge priority-${(ticket?.priority || 'medium')?.toLowerCase()}">${ticket?.priority || 'Medium'}</span>
        </div>
        <div class="meta-card">
          <h3>Status</h3>
          <span class="status-badge">${ticket?.status || 'Pending'}</span>
        </div>
        <div class="meta-card">
          <h3>Scheduled Date</h3>
          <p>${formatDate(ticket?.scheduledDate)}</p>
        </div>
        <div class="meta-card">
          <h3>Estimated Duration</h3>
          <p>${ticket?.estimatedDuration || 'TBD'}</p>
        </div>
      </div>

      ${ticket?.technicians?.length > 0 ? `
      <div style="margin-bottom:24px;">
        <div class="section-title">Assigned Technicians</div>
        <div class="tech-list">
          ${ticket?.technicians?.map(t => `<span class="tech-chip">${t}</span>`)?.join('')}
        </div>
      </div>` : ''}

      ${ticket?.description ? `
      <div class="desc-box">
        <h3>Description</h3>
        <p>${ticket?.description}</p>
      </div>` : ''}

      ${products?.length > 0 ? `
      <div class="section-title">Products &amp; Services</div>
      <table>
        <thead>
          <tr>
            <th>Description</th>
            <th>Qty</th>
            <th>Unit Price</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          ${products?.map(p => `
          <tr>
            <td>${p?.name}</td>
            <td>${p?.quantity}</td>
            <td>${formatCurrency(p?.price)}</td>
            <td>${formatCurrency(parseFloat(p?.price || 0) * parseInt(p?.quantity || 1))}</td>
          </tr>`)?.join('')}
          <tr class="total-row">
            <td colspan="3" style="text-align:right;">Estimated Total</td>
            <td class="total-amount">${formatCurrency(total)}</td>
          </tr>
        </tbody>
      </table>` : ''}

      <div class="approval-section">
        <h3>Customer Approval</h3>
        <p style="font-size:13px;color:#374151;margin-bottom:16px;">By signing below, you authorize ${companyName} to proceed with the described service work at the quoted price.</p>
        <div class="sig-grid">
          <div class="sig-box">
            <div class="label">Customer Signature &amp; Date</div>
          </div>
          <div class="sig-box">
            <div class="label">${companyName} Representative</div>
          </div>
        </div>
      </div>

      <div class="footer">
        ${footerText || `${companyName} &bull; Generated ${new Date()?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`}
      </div>
    </body>
    </html>
  `;

  return html;
};

export const downloadPDF = (htmlContent, filename) => {
  const printWindow = window.open('', '_blank', 'width=900,height=700');
  if (!printWindow) {
    alert('Please allow popups to download the PDF.');
    return;
  }
  printWindow?.document?.write(htmlContent);
  printWindow?.document?.close();
  printWindow?.focus();
  setTimeout(() => {
    printWindow?.print();
  }, 500);
};
